import type { Source, CustomerType } from 'src/pages/reports/common/consts';

import { useMemo, useContext, createContext } from 'react';

import { useFilters } from 'src/hooks/useFilters';

import { ROLES } from 'src/lib/consts';

import { useAuthContext } from 'src/auth/hooks';

export type TFilters = {
  locationIds: string[];
  dateFrom: string;
  dateTo: string;
  source: Source;
  customerType?: CustomerType;
  eventId?: string;
  threshold?: number;
  repeatType?: string;
};

type TFiltersContext = {
  filters: TFilters;
  resetFilters: () => void;
  updateFilters: (newFilters: Partial<TFilters>) => void;
  availableLocationIds: string[]; // Add this to expose available locations
};

const FiltersContext = createContext<TFiltersContext>({
  filters: {
    locationIds: [],
    dateFrom: '',
    dateTo: '',
    source: 'combined',
    customerType: 'all',
  },
  resetFilters: () => {},
  updateFilters: () => {},
  availableLocationIds: [],
});

export const useFiltersContext = () => useContext(FiltersContext);

export const FiltersProvider = ({
  children,
  defaultFilters,
  enforceLocationAccess = true, // New prop to enable/disable location restrictions
}: {
  children: React.ReactNode;
  defaultFilters?: TFilters;
  enforceLocationAccess?: boolean;
}) => {
  const { user } = useAuthContext();
  const { filters, resetFilters, updateFilters } = useFilters({
    defaultFilters,
    enforceLocationAccess,
  });

  // Calculate available location IDs based on user permissions
  const availableLocationIds = useMemo(() => {
    if (!enforceLocationAccess || !user) {
      return filters.locationIds; // Return all if no restrictions
    }

    // Super admin has access to all locations
    if (user.role === ROLES.SUPER_ADMIN || user.role === ROLES.INSIGHTS) {
      return filters.locationIds;
    }

    // For other users, filter to only approved locations
    return filters.locationIds.filter((locationId) =>
      user.approved_locations?.includes(locationId)
    );
  }, [filters.locationIds, user, enforceLocationAccess]);

  // Create filtered filters object
  const filteredFilters = useMemo(
    () => ({
      ...filters,
      locationIds: availableLocationIds,
    }),
    [filters, availableLocationIds]
  );

  // Enhanced updateFilters that respects location restrictions
  const restrictedUpdateFilters = (newFilters: Partial<TFilters>) => {
    if (newFilters.locationIds && enforceLocationAccess && user?.role === ROLES.MANAGER) {
      // Filter out any location IDs the user doesn't have access to
      const allowedLocationIds = newFilters.locationIds.filter((locationId) =>
        user?.approved_locations?.includes(locationId)
      );
      newFilters = {
        ...newFilters,
        locationIds: allowedLocationIds,
      };
    }
    updateFilters(newFilters);
  };

  return (
    <FiltersContext.Provider
      value={{
        filters: filteredFilters,
        resetFilters,
        updateFilters: restrictedUpdateFilters,
        availableLocationIds,
      }}
    >
      {children}
    </FiltersContext.Provider>
  );
};
